import * as $ from "jquery";
import * as ko from "knockout";
import * as _ from "underscore";
import * as localisationHelper from 'localisationHelper';

// Services
import { ConfigurableTextService } from "Scripts/ts/Localisation/ConfigurableTextService";

export const Deps = {
    LocalisationHelper: localisationHelper
};

class AccessibilityBindings {
    constructor() {

        const AriaLabelAttribute = "aria-label";

        ko.bindingHandlers.contentVisible = {
            update: (element: any, valueAccessor: any) => {
                const shouldDisplayContainer = ko.utils.unwrapObservable(valueAccessor());

                const ele = $(element)
                    .attr("aria-live", "polite")
                    .attr("aria-hidden", shouldDisplayContainer ? "false" : "true");

                if (shouldDisplayContainer) {
                    $(ele).show();
                } else {
                    $(ele).hide();
                }
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-hidden
        ko.bindingHandlers.ariaHidden = {
            update: (element: any, valueAccessor: any) => {
                $(element).attr("aria-hidden", ko.utils.unwrapObservable(valueAccessor()));
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-disabled
        ko.bindingHandlers.ariaDisabled = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-disabled", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-readonly
        ko.bindingHandlers.ariaReadOnly = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-readonly", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-required
        ko.bindingHandlers.ariaRequired = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-required", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-invalid
        ko.bindingHandlers.ariaInvalid = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-invalid", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-describedby
        ko.bindingHandlers.ariaDescribedBy = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-describedby", value);
            }
        };

        ko.bindingHandlers.ariaDescription = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-description", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-labelledby
        ko.bindingHandlers.ariaLabelledBy = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-labelledby", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria-practices-1.1/examples/breadcrumb/index.html
        ko.bindingHandlers.ariaCurrent = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                if (value != null && value != undefined && value) {
                    $(element).attr("aria-current", "page");
                }
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-label
        // Purpose:     Retrieves resource content
        ko.bindingHandlers.locAriaLabel = {
            update: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
                const allBindings = allBindingsAccessor();

                if (allBindings.locAriaLabel.ResourceName == null) {
                    // Note: Do not use this version, it will eventually be removed as soon as the resources
                    // have been moved into the new structure.
                    const value = this.localisationFunction(valueAccessor, allBindingsAccessor);
                    $(element).attr(AriaLabelAttribute, value);
                } else {
                    const resourceName = allBindings.locAriaLabel.ResourceName;
                    const section = allBindings.SectionName || "AccessibilityResources";

                    $(element).attr(AriaLabelAttribute,
                        localisationHelper.getString(section, resourceName) || "");
                }
            }
        };


        // Purpose:     Retrieves resource content
        ko.bindingHandlers.locAriaDescription = {
            update: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
                const value = this.localisationFunction(valueAccessor, allBindingsAccessor);
                $(element).attr("aria-description", value);
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-label
        // Purpose:     Content has already been retrieve via other means so only needs the container updating
        ko.bindingHandlers.ariaLabel = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                if (value) {
                    $(element).attr(AriaLabelAttribute, value);
                }
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-checked
        ko.bindingHandlers.ariaChecked = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-checked", value);
            }
        };

        // Source:      https://www.w3.org/WAI/tutorials/tables/caption-summary/
        ko.bindingHandlers.accessibilitySummary = {
            update: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
                const allBindings = allBindingsAccessor();
                const resourceName = allBindings.accessibilitySummary.ResourceName;
                const section = allBindings.accessibilitySummary.SectionName || "AccessibilityResources";

                $(element).attr("summary",
                    localisationHelper.getString(section, resourceName) || "");
            }
        };

        // Source:      https://www.w3.org/TR/wai-aria/#aria-controls
        ko.bindingHandlers.ariaControls = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("aria-controls", value);
            }
        };

        ko.bindingHandlers.containerReadOnly = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("readonly", value);
            }
        };

        ko.bindingHandlers.containerDisabled = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("disabled", value);
            }
        };

        // Note: If you're using this binding, you should ensure the text is already translated in the
        //       calling component.
        ko.bindingHandlers.containerTitle = {
            update: (element: any, valueAccessor: any) => {
                const value = ko.utils.unwrapObservable(valueAccessor());
                $(element).attr("title", value);
            }
        };
    }

    private displayBlankResponse(section: string) {
        return section === 'Macro';
    }

    private localiseText(valueAccessor: any, allBindingsAccessor: any) {
        const value = ko.utils.unwrapObservable(valueAccessor());
        const allBindings = allBindingsAccessor();
        const section = allBindings.section || 'Resources';
        const errorParams = allBindings.locparams || null;

        let output = 'NOT_LOCALISED';
        if (value !== undefined && value !== null && value.length > 0) {
            if (section === 'Macro') {
                // go to the new macro endpoint
                output = ConfigurableTextService.RetrieveTextSync(value);
            }
            else {
                output = localisationHelper.getString(section, value);
            }

            if (!output) {
                // Note: If a macro was provided as the resource and that returns null then try and fallback to a
                //       different resource. If that returns null then leave as 'NOT_LOCALISED'
                if (allBindings.defaultValue != null) {
                    const defaultSection = allBindings.defaultValue.section || 'Resources';
                    output = localisationHelper.getString(defaultSection, allBindings.defaultValue.loctext);
                }

                if ((output == null || output === '') && this.displayBlankResponse(section)) {
                    output = '';
                } else {
                    output = output || 'NOT_LOCALISED';
                }
            } else if (errorParams !== null) {

                errorParams.foreach((element: any, index: number) => {
                    output = output.replace(`{${index}}`, element);
                })
            }
        }

        return output;
    }

    private localisationFunction(valueAccessor: any, allBindingsAccessor: any) {
        const output = this.localiseText(valueAccessor, allBindingsAccessor);

        return output.replace('{0}', ko.unwrap(allBindingsAccessor().locvalue));
    }
}

const service = new AccessibilityBindings();
export { service as AccessibilityBindings };